/**
 * What each kind of check needs pointing at, and how to tell somebody it is not pointed anywhere.
 *
 * THE PROBLEM THIS SOLVES IS SILENT REFUSAL. Five of the ten checks need a page or a
 * list of terms, and until there was a way to set one they were created empty and
 * refused on every run — "the probe has no target url", once a day, forever. The
 * feed said nothing because nothing had gone wrong; the check was simply aimed at
 * nothing. `missingFor` is what turns that into a sentence on the card.
 *
 * Pure and dependency-free so CRA's jest can test it, the same reason `tierTone.ts`
 * and `watchFeed.ts` are their own modules.
 *
 * THE FIELD LIST IS HERE RATHER THAN SERVED, deliberately and unlike the probe
 * catalogue itself. A field is a form control — a url box, a term list, a country
 * picker — and the shape of a control is a frontend fact. The backend already says
 * which KINDS exist and which apply to a subject; it should not also be describing
 * text inputs.
 */
import type { WatchProbe } from '../../types';

export type FieldKind = 'url' | 'terms' | 'geo' | 'keywords' | 'engine';

export interface FieldSpec {
  field: FieldKind;
  label: string;
  /** Placeholder, and a real example rather than a description of one. */
  placeholder: string;
  /** Why this check wants that, in one line. */
  hint: string;
  /** Without this the check cannot run at all. */
  required: boolean;
}

const URL_FIELD = (label: string, placeholder: string, hint: string): FieldSpec => ({
  field: 'url',
  label,
  placeholder,
  hint,
  required: true,
});

const TERMS: FieldSpec = {
  field: 'terms',
  label: 'Terms',
  placeholder: 'property management crm, letting agent software',
  hint: "What the category is called. Left empty it uses the watch's own name and aliases.",
  required: false,
};

const GEO: FieldSpec = {
  field: 'geo',
  label: 'Country',
  placeholder: 'US',
  hint: 'A two-letter code. Interest curves are country-specific, so a worldwide one answers nobody.',
  required: false,
};

export const FIELDS: Record<string, FieldSpec[]> = {
  page_content: [
    URL_FIELD(
      'Page',
      'https://acme.com/pricing',
      'The one page to watch for rewrites. One check per page.',
    ),
  ],
  offer: [
    URL_FIELD(
      'Pricing page',
      'https://acme.com/pricing',
      'Where their prices are listed. Most pricing pages publish no structured prices, so this often gets read by a model.',
    ),
  ],
  jobs: [
    URL_FIELD(
      'Careers page',
      'https://acme.com/careers',
      'The page that lists open roles - the one with the jobs ON it, not the one that links to it.',
    ),
  ],
  reviews: [
    URL_FIELD(
      'Review page',
      'https://www.g2.com/products/acme/reviews',
      'One check per review site, so a collapse on one is not hidden behind a good score on another.',
    ),
  ],
  serp_rank: [
    {
      field: 'keywords',
      label: 'Keywords',
      placeholder: 'practicevital reviews, practicevital pricing',
      hint: 'Separate searches with commas. Each one is looked up on every check, so keep to the ones you compete on (up to 25).',
      required: true,
    },
    {
      field: 'engine',
      label: 'Search engine',
      placeholder: 'Chosen on the next check',
      hint: 'Every search in this check uses this engine, so each week compares the same results page. Changing it starts the history again.',
      required: false,
    },
  ],
  trends: [TERMS, GEO],
  demand: [TERMS],
};

/** The fields this kind of check takes. Empty for the ones that need nothing. */
export function fieldsFor(kind: string): FieldSpec[] {
  return FIELDS[kind] ?? [];
}

/**
 * Why this check cannot run yet, in a sentence — or null when it can.
 *
 * THE POINT OF THE WHOLE MODULE. A check aimed at nothing is indistinguishable from
 * a check finding nothing, and only one of those is fine.
 */
export function missingFor(probe: WatchProbe): string | null {
  for (const spec of fieldsFor(probe.kind)) {
    if (!spec.required) continue;
    if (spec.field === 'url' && !probe.target.trim()) {
      return `This check needs a page. Give it the ${spec.label.toLowerCase()} and it will start comparing.`;
    }
    if (spec.field === 'keywords' && !keywordsOf(probe).length) {
      return 'This check needs some keywords before it can look anything up.';
    }
  }
  return null;
}

/** The keyword ids on a probe's config, defensively. */
export function keywordsOf(probe: WatchProbe): number[] {
  const raw = probe.config?.keyword_ids;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v) && v > 0);
}

/** The terms on a probe's config, defensively. */
export function termsOf(probe: WatchProbe): string[] {
  const raw = probe.config?.terms;
  if (!Array.isArray(raw)) return [];
  return raw.map((v) => String(v).trim()).filter(Boolean);
}

/** The search engine a rank check is set to; empty until its first check picks one. */
export function engineOf(probe: WatchProbe): string {
  const raw = probe.config?.engine;
  return typeof raw === 'string' ? raw : '';
}

export function geoOf(probe: WatchProbe): string {
  const raw = probe.config?.geo;
  return typeof raw === 'string' ? raw.trim().toUpperCase() : '';
}

/**
 * A comma-separated box into a term list.
 *
 * Deduplicated case-insensitively while KEEPING the first spelling: the terms are sent
 * to Google as written, and a user who typed "CRM" meant "CRM".
 */
export function parseTerms(value: string, limit = 10): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const part of value.split(',')) {
    const term = part.trim().replace(/\s+/g, ' ');
    if (!term) continue;
    const key = term.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(term);
    if (out.length >= limit) break;
  }
  return out;
}

/**
 * Whether an edit changes WHAT a check watches rather than how.
 *
 * Mirrors the server's own rule (`_identity_changed`), and it is here so the form can
 * WARN before saving: re-pointing a check starts its history again, and somebody
 * correcting a typo should know that costs them a quiet run rather than discovering it
 * afterwards.
 */
export function isRepointing(
  probe: WatchProbe,
  next: { target?: string; terms?: string[]; geo?: string; keywords?: number[]; engine?: string },
): boolean {
  if (next.target !== undefined && next.target.trim() !== probe.target.trim()) return true;
  if (next.geo !== undefined && next.geo.trim().toUpperCase() !== geoOf(probe)) return true;
  if (next.engine !== undefined && next.engine !== engineOf(probe)) return true;
  if (next.terms !== undefined && !sameStrings(next.terms, termsOf(probe))) return true;
  if (next.keywords !== undefined && !sameNumbers(next.keywords, keywordsOf(probe))) return true;
  return false;
}

/** The same terms, ignoring case, spacing and order. Pure. */
export function sameStrings(a: string[], b: string[]): boolean {
  const norm = (xs: string[]) => xs.map((x) => x.trim().toLowerCase()).sort();
  const [x, y] = [norm(a), norm(b)];
  return x.length === y.length && x.every((v, i) => v === y[i]);
}

function sameNumbers(a: number[], b: number[]): boolean {
  const [x, y] = [[...a].sort((m, n) => m - n), [...b].sort((m, n) => m - n)];
  return x.length === y.length && x.every((v, i) => v === y[i]);
}

/** Does this look like a web address? Mirrors the server's cheap check so the form can say so
 *  before a round trip — the mistake it catches is a pasted page TITLE. */
export function looksLikeUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!/^https?:\/\//i.test(trimmed)) return false;
  try {
    return Boolean(new URL(trimmed).hostname);
  } catch {
    return false;
  }
}

/**
 * Typed text into a keyword list: each finished entry (before a comma, or all
 * of it when `finish`) is added once, lowercased like the stored keywords, up to
 * `limit`; what is still being typed is handed back. Pure.
 */
export function addTyped(
  list: string[],
  text: string,
  limit: number,
  finish = false,
): { list: string[]; draft: string } {
  const parts = text.split(',');
  const draft = finish ? '' : parts.pop() ?? '';
  const out = [...list];
  const seen = new Set(out.map((k) => k.toLowerCase()));
  for (const part of parts) {
    const kw = part.trim().replace(/\s+/g, ' ').toLowerCase();
    if (!kw || seen.has(kw) || out.length >= limit) continue;
    seen.add(kw);
    out.push(kw);
  }
  return { list: out, draft };
}
